// Rule (AIAgent_Rules) sub-entity client — restores the "Rules" Agent tab dropped in the
// 2026-08-19/20 AgentEditor.tsx refactor. Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BaseRuleController), verified by reading the controller and
// entity directly.
//
// Route confirmed as documented: base route is `/api/v1/ai-agent/rules`, NOT
// `/api/v1/ai-agent/agent-rules` (the old deleted client's and the design doc's shared wrong
// assumption) — `[Route("api/v1/ai-agent/rules")]` on BaseRuleController.cs, verified directly.
//
// Fields confirmed as documented: the real Rule entity has RuleName/RuleDescription/Enabled/
// AgentRuleTypeID/TriggerEventID/AppliedOnAttribute1-9 — not RulePriority/RuleExpression/
// IsActive (neither of which exists on the entity at all). See types.ts's AgentRuleDTO comment.
//
// getRulesByAgent posts to `by-agent` (BaseRuleController's GetByAgent action, bound to a
// GetByAgentSearchRequest carrying an IDInfo AgentID) — same `{ AgentID: { ID } }` root-level
// shape every other by-agent/by-agent-id query in this package already uses (see
// agentAdminApiClient.getInstructionsByAgent, agentTemplateAdminApiClient.getTemplatesByAgent).

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentRuleDTO, AgentRuleFormValues } from '../types';

export class AgentRuleAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getRulesByAgent(agentId: number): Promise<AdminApiResult<AgentRuleDTO[]>> {
    const result = await this.postQuery<AgentRuleDTO[]>('/api/v1/ai-agent/rules/by-agent', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createRule(agentId: number, rule: AgentRuleFormValues): Promise<AdminApiResult<AgentRuleDTO>> {
    return this.post<AgentRuleDTO>('/api/v1/ai-agent/rules', { AgentID: agentId, ...rule });
  }

  async updateRule(agentRuleId: number, rule: Partial<AgentRuleFormValues>): Promise<AdminApiResult<AgentRuleDTO>> {
    return this.put<AgentRuleDTO>(`/api/v1/ai-agent/rules/${agentRuleId}`, {
      AgentRuleID: agentRuleId,
      ...rule,
    });
  }

  async deleteRule(agentRuleId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/rules/${agentRuleId}`, { id: agentRuleId });
  }
}
